import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

const CORS_PROXIES = [
  (url) => url, // try direct first
  (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url) => `https://r.jina.ai/${url.startsWith('http') ? url : `https://${url}`}`,
];

// Exam-specific marking schemes
const EXAM_MARKING_SCHEMES = {
  'JEE': {
    'MCQ': { correct: 4, incorrect: -1, unattempted: 0 },
    'INTEGER': { correct: 4, incorrect: 0, unattempted: 0 },
    'NUMERICAL': { correct: 4, incorrect: 0, unattempted: 0 }
  },
  'NEET': {
    'MCQ': { correct: 4, incorrect: -1, unattempted: 0 }
  },
  'CAT': {
    'MCQ': { correct: 3, incorrect: -1, unattempted: 0 }
  },
  'GATE': {
    'MCQ': { correct: 1, incorrect: -0.33, unattempted: 0 },
    'MSQ': { correct: 2, incorrect: 0, unattempted: 0 },
    'NAT': { correct: 1, incorrect: 0, unattempted: 0 }
  },
  'DEFAULT': {
    'MCQ': { correct: 1, incorrect: 0, unattempted: 0 }
  }
};

const normalize = (value = '') => value.replace(/\s+/g, ' ').trim();

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const looksLikeQuestionHeader = (headers) => {
  const headerText = headers.map((h) => h.toLowerCase());
  return (
    headerText.some((h) => h.includes('question')) &&
    (headerText.some((h) => h.includes('status')) ||
      headerText.some((h) => h.includes('answer')) ||
      headerText.some((h) => h.includes('option')))
  );
};

const buildHeaderMap = (cells) => {
  const map = {};
  cells.forEach((cell, idx) => {
    const text = normalize(cell.textContent).toLowerCase();
    if (text.includes('section') || text.includes('subject')) map.section = idx;
    if (text.includes('question')) map.question = idx;
    if (text.includes('status')) map.status = idx;

    // User response headers
    if (text.includes('chosen option') || text.includes('option chosen')) map.chosenOption = idx;
    if (text.includes('option marked') || text.includes('marked option') || text.includes('selected option'))
      map.chosenOption = idx;
    if (text.includes('chosen') || text.includes('your answer') || text.includes('given answer')) map.chosenAnswer = idx;
    if (text.includes('response') || text.includes('candidate answer') || text.includes('candidate response'))
      map.chosenAnswer = idx;
    if (text.includes('answer')) map.answer = idx;
    if (text.includes('subjective')) map.subjectiveAnswer = idx;

    // Correct/official answer headers
    if (text.includes('possible answer') || text.includes('correct option') || text.includes('correct ans'))
      map.possibleAnswer = idx;
    if (text.includes('correct') || text.includes('right') || text.includes('official answer')) map.correct = idx;
    if (text.includes('actual answer')) map.correct = idx;

    // Marks headers
    if (text.includes('marks obtained') || text.includes('marks scored')) map.marks = idx;
    if (text === 'marks' || text === 'score') map.marks = idx;
    if (text.includes('marks for correct') || text.includes('correct marks')) map.correctMarks = idx;
    if (text.includes('marks for incorrect') || text.includes('negative')) map.incorrectMarks = idx;
    if (text.includes('unattempt') && text.includes('marks')) map.unattemptedMarks = idx;
    if (text.includes('max marks') || text === 'marks for correct') map.maxMarks = idx;
  });
  return map;
};

const extractCandidateDetails = (doc) => {
  const details = {};
  const rows = Array.from(doc.querySelectorAll('table tr'));
  rows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length !== 2) return;
    const key = normalize(cells[0].textContent).toLowerCase();
    const value = normalize(cells[1].textContent);
    if (key.includes('candidate name')) details.name = value;
    else if (key.includes('roll') || key.includes('reg') || key.includes('application')) details.roll = value;
    else if (key.includes('test name') || key.includes('exam name')) details.testName = value;
    else if (key.includes('test id') || key.includes('assessment')) details.assessmentId = value;
  });
  return details;
};

const normalizeOptionValue = (raw) => {
  const val = normalize(raw).toLowerCase();
  if (!val) return '';
  // Extract number/letter from patterns like "Option 1", "Option A", "choice - 2"
  const numMatch = val.match(/(?:option|choice)?\s*([0-9]+(\.[0-9]+)?)/i);
  if (numMatch) return numMatch[1];
  const letterMatch = val.match(/(?:option|choice)?\s*([a-d])/i);
  if (letterMatch) return letterMatch[1];
  return val;
};

const normalizeForCompare = (value = '') => normalize(value).replace(/\s+/g, '').toLowerCase();

// Enhanced answer matching for descriptive questions
const calculateSimilarity = (str1, str2) => {
  if (!str1 || !str2) return 0;
  
  const s1 = normalizeForCompare(str1);
  const s2 = normalizeForCompare(str2);
  
  if (s1 === s2) return 1;
  
  // Check for common patterns in descriptive answers
  const commonWords = ['the', 'a', 'an', 'is', 'are', 'was', 'were', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with'];
  
  // Split into words and filter out common words for better matching
  const words1 = s1.split(/\s+/).filter(w => w.length > 2 && !commonWords.includes(w));
  const words2 = s2.split(/\s+/).filter(w => w.length > 2 && !commonWords.includes(w));
  
  // Calculate word-level similarity
  let matchingWords = 0;
  const totalWords = Math.max(words1.length, words2.length);
  
  if (totalWords === 0) return 0;
  
  words1.forEach(word1 => {
    const bestMatch = Math.max(...words2.map(word2 => {
      if (word1 === word2) return 1;
      if (word1.includes(word2) || word2.includes(word1)) return 0.8;
      return levenshteinSimilarity(word1, word2);
    }));
    if (bestMatch > 0.7) matchingWords++;
  });
  
  const wordSimilarity = matchingWords / totalWords;
  
  // Also calculate character-level similarity as fallback
  const charSimilarity = levenshteinSimilarity(s1, s2);
  
  // Return the higher of the two similarities
  return Math.max(wordSimilarity, charSimilarity);
};

// Levenshtein distance similarity calculation
const levenshteinSimilarity = (str1, str2) => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + cost
      );
    }
  }
  
  const maxLen = Math.max(str1.length, str2.length);
  return maxLen === 0 ? 1 : (maxLen - matrix[str2.length][str1.length]) / maxLen;
};

// Detect exam type from response sheet content
const detectExamType = (doc, details) => {
  const content = doc.textContent.toLowerCase();
  const testName = (details.testName || '').toLowerCase();
  
  if (content.includes('jee') || testName.includes('jee')) return 'JEE';
  if (content.includes('neet') || testName.includes('neet')) return 'NEET';
  if (content.includes('cat') || testName.includes('cat')) return 'CAT';
  if (content.includes('gate') || testName.includes('gate')) return 'GATE';
  
  return 'DEFAULT';
};

// Detect question type from the question content
const detectQuestionType = (questionText, chosenAnswer, correctAnswer) => {
  const text = (questionText || '').toLowerCase();
  
  // Check for numerical/integer type questions
  if (text.includes('integer') || text.includes('numerical')) {
    return 'INTEGER';
  }
  
  // Check if answers are numeric (for numerical answer type)
  const chosenNum = parseFloat(chosenAnswer);
  const correctNum = parseFloat(correctAnswer);
  if (!isNaN(chosenNum) && !isNaN(correctNum)) {
    return 'NUMERICAL';
  }
  
  // Check for multiple select questions
  if (text.includes('select all') || text.includes('multiple correct')) {
    return 'MSQ';
  }
  
  // Default to MCQ
  return 'MCQ';
};

const extractQuestions = (doc, examType = 'DEFAULT') => {
  const questionRows = [];
  const tables = Array.from(doc.querySelectorAll('table'));

  tables.forEach((table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length < 3) return;

    const headerRow =
      rows.find((r) => r.querySelectorAll('th').length >= 3) ||
      rows.find((r) => looksLikeQuestionHeader(Array.from(r.querySelectorAll('td')).map((c) => normalize(c.textContent))));
    if (!headerRow) return;

    const headerCells = Array.from(headerRow.querySelectorAll('th, td'));
    const headers = headerCells.map((c) => normalize(c.textContent));
    if (!looksLikeQuestionHeader(headers)) return;

    const headerMap = buildHeaderMap(headerCells);
    const startIndex = rows.indexOf(headerRow) + 1;

    rows.slice(startIndex).forEach((row, index) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length < 3) return;

      const get = (key) => (headerMap[key] != null ? normalize(cells[headerMap[key]]?.textContent) : '');
      const status = get('status');
      const questionText = get('question') || '';
      const chosenRaw =
        get('chosenOption') ||
        get('chosenAnswer') ||
        get('subjectiveAnswer') ||
        get('response') ||
        get('answer') ||
        '';
      const possibleRaw = get('possibleAnswer') || get('correct') || '';

      const chosenDisplay = normalize(chosenRaw);
      const correctDisplay = normalize(possibleRaw);
      const chosen = normalizeOptionValue(chosenRaw);
      const correct = normalizeOptionValue(possibleRaw);

      // Detect question type for better marking scheme application
      const questionType = detectQuestionType(questionText, chosenDisplay, correctDisplay);
      
      // Get exam-specific marking scheme
      const markingScheme = EXAM_MARKING_SCHEMES[examType]?.[questionType] || 
                           EXAM_MARKING_SCHEMES['DEFAULT']['MCQ'];

      const marksAwarded = toNumber(get('marks'));
      const marksForCorrect = toNumber(get('correctMarks')) ?? toNumber(get('maxMarks')) ?? markingScheme.correct;
      const marksForIncorrect = toNumber(get('incorrectMarks')) ?? markingScheme.incorrect;
      const marksForUnattempted = toNumber(get('unattemptedMarks')) ?? markingScheme.unattempted;
      const section = get('section') || 'General';

      const isBlank =
        chosenDisplay === '' ||
        /not\s*(answered|attempted|visited)/i.test(chosenDisplay) ||
        /not\s*(answered|attempted|visited)/i.test(status);

      const attempted = !isBlank;
      
      // Enhanced answer matching logic
      let isCorrect = false;
      let similarity = 0;
      
      if (attempted && chosenDisplay && correctDisplay) {
        // For numerical answers, check if values are close
        const chosenNum = parseFloat(chosenDisplay);
        const correctNum = parseFloat(correctDisplay);
        
        if (!isNaN(chosenNum) && !isNaN(correctNum)) {
          // For numerical answers, allow small tolerance
          const tolerance = Math.abs(correctNum) * 0.01; // 1% tolerance
          isCorrect = Math.abs(chosenNum - correctNum) <= Math.max(tolerance, 0.01);
          similarity = isCorrect ? 1 : 0;
        } else {
          // For text answers, use similarity matching
          similarity = calculateSimilarity(chosenDisplay, correctDisplay);
          
          // Consider correct if similarity > 80% or exact match after normalization
          isCorrect = similarity > 0.8 || 
                     normalizeForCompare(chosen || chosenDisplay) === normalizeForCompare(correct || correctDisplay);
        }
      }

      let inferredMarks = 0;

      if (marksAwarded != null) {
        inferredMarks = marksAwarded;
        // Override isCorrect based on marks if available
        isCorrect = marksAwarded > 0;
      } else if (attempted) {
        inferredMarks = isCorrect ? marksForCorrect : marksForIncorrect;
      } else {
        inferredMarks = marksForUnattempted;
      }

      questionRows.push({
        questionNumber: index + 1,
        section,
        questionType,
        status: status || (attempted ? (isCorrect ? 'Correct' : 'Incorrect') : 'Unattempted'),
        chosen: chosenDisplay || chosen,
        correct: correctDisplay || correct,
        chosenKey: chosen,
        correctKey: correct,
        isCorrect,
        marks: inferredMarks,
        attempted,
        similarity: similarity.toFixed(2),
        markingScheme: {
          correct: marksForCorrect,
          incorrect: marksForIncorrect,
          unattempted: marksForUnattempted
        }
      });
    });
  });

  return questionRows;
};

const summarize = (questions) => {
  const summary = {
    totalQuestions: questions.length,
    attempted: 0,
    unattempted: 0,
    correct: 0,
    incorrect: 0,
    totalScore: 0,
    sections: {},
  };

  questions.forEach((q) => {
    if (q.attempted) {
      summary.attempted += 1;
      summary.correct += q.isCorrect ? 1 : 0;
      summary.incorrect += q.isCorrect ? 0 : 1;
    }
    summary.totalScore += Number(q.marks) || 0;
    const section = q.section || 'General';
    if (!summary.sections[section]) {
      summary.sections[section] = { questions: 0, attempted: 0, score: 0, correct: 0, incorrect: 0 };
    }
    summary.sections[section].questions += 1;
    summary.sections[section].attempted += q.attempted ? 1 : 0;
    summary.sections[section].correct += q.isCorrect ? 1 : 0;
    summary.sections[section].incorrect += q.attempted && !q.isCorrect ? 1 : 0;
    summary.sections[section].score += Number(q.marks) || 0;
  });

  summary.unattempted = summary.totalQuestions - summary.attempted;
  return summary;
};

const ScoreCalculator = () => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [htmlSource, setHtmlSource] = useState('');
  const [details, setDetails] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showMarkingSchemeEditor, setShowMarkingSchemeEditor] = useState(false);
  const [customMarkingSchemes, setCustomMarkingSchemes] = useState({});

  const summary = useMemo(() => summarize(questions), [questions]);

  // Recalculate scores with custom marking schemes
  const recalculateWithCustomSchemes = () => {
    const updatedQuestions = questions.map(q => {
      const customScheme = customMarkingSchemes[q.questionType];
      if (customScheme) {
        let newMarks = 0;
        if (q.attempted) {
          newMarks = q.isCorrect ? customScheme.correct : customScheme.incorrect;
        } else {
          newMarks = customScheme.unattempted;
        }
        return {
          ...q,
          marks: newMarks,
          markingScheme: customScheme
        };
      }
      return q;
    });
    setQuestions(updatedQuestions);
    toast.success('Scores recalculated with custom marking schemes');
  };

  // Export results to CSV
  const exportToCSV = () => {
    const headers = [
      'Question Number', 'Section', 'Question Type', 'Status', 
      'Your Answer', 'Correct Answer', 'Match %', 'Result', 'Marks'
    ];
    
    const csvData = questions.map(q => [
      q.questionNumber,
      q.section,
      q.questionType,
      q.status,
      q.chosen || '-',
      q.correct || '-',
      q.attempted ? `${(parseFloat(q.similarity) * 100).toFixed(0)}%` : '-',
      q.attempted ? (q.isCorrect ? 'Correct' : 'Incorrect') : 'Unattempted',
      q.marks
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `score_analysis_${details?.roll || 'results'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Results exported to CSV');
  };

  const fetchHtml = async () => {
    setError('');
    let lastErr = null;
    for (const makeUrl of CORS_PROXIES) {
      const target = makeUrl(sheetUrl.trim());
      try {
        const res = await fetch(target);
        if (!res.ok) {
          lastErr = new Error(`Request failed with ${res.status}`);
          continue;
        }
        const text = await res.text();
        if (text && text.length > 50) return text;
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr || new Error('Unable to fetch response sheet');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sheetUrl.trim()) {
      setError('Please paste a TCS iON response sheet URL.');
      return;
    }

    setLoading(true);
    try {
      const html = await fetchHtml();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const candidate = extractCandidateDetails(doc);
      
      // Detect exam type from the response sheet
      const examType = detectExamType(doc, candidate);
      
      const qs = extractQuestions(doc, examType);

      if (!qs.length) {
        throw new Error('Could not detect any questions. Please verify the URL.');
      }

      setHtmlSource(html);
      setDetails({ ...candidate, examType });
      setQuestions(qs);
      toast.success(`Response sheet parsed successfully (${examType} exam detected)`);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while parsing.');
      toast.error('Could not parse the response sheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Enhanced TCS iON Score Calculator
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Paste your TCS iON response sheet URL. Our enhanced calculator automatically detects exam types (JEE, NEET, CAT, GATE), 
            applies appropriate marking schemes, uses intelligent answer matching for descriptive questions, and provides detailed analysis.
          </p>
          
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">✨ Enhanced Features</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Auto-detects exam type and applies correct marking schemes</li>
              <li>• Intelligent matching for descriptive answers with similarity scoring</li>
              <li>• Support for MCQ, Integer, Numerical, and MSQ question types</li>
              <li>• Customizable marking schemes with real-time recalculation</li>
              <li>• Detailed question-wise analysis with match percentages</li>
              <li>• Export results to CSV for further analysis</li>
            </ul>
          </div>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <label className="block text-sm font-medium text-gray-700">
              Response sheet URL
            </label>
            <input
              type="url"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://cdn.digialm.com/.../yourResponse.html"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Processing…' : 'Calculate Score'}
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        {details && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Candidate Details</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {details.name && (
                <>
                  <dt className="font-medium text-gray-700">Name</dt>
                  <dd className="text-gray-900">{details.name}</dd>
                </>
              )}
              {details.roll && (
                <>
                  <dt className="font-medium text-gray-700">Roll / Reg No</dt>
                  <dd className="text-gray-900">{details.roll}</dd>
                </>
              )}
              {details.testName && (
                <>
                  <dt className="font-medium text-gray-700">Test Name</dt>
                  <dd className="text-gray-900">{details.testName}</dd>
                </>
              )}
              {details.assessmentId && (
                <>
                  <dt className="font-medium text-gray-700">Assessment ID</dt>
                  <dd className="text-gray-900">{details.assessmentId}</dd>
                </>
              )}
              {details.examType && (
                <>
                  <dt className="font-medium text-gray-700">Exam Type</dt>
                  <dd className="text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {details.examType}
                    </span>
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}

        {questions.length > 0 && (
          <>
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                <div className="p-3 rounded-md bg-gray-100">
                  <p className="text-xs text-gray-600">Questions</p>
                  <p className="text-xl font-semibold">{summary.totalQuestions}</p>
                </div>
                <div className="p-3 rounded-md bg-green-50">
                  <p className="text-xs text-gray-600">Attempted</p>
                  <p className="text-xl font-semibold text-green-700">{summary.attempted}</p>
                </div>
                <div className="p-3 rounded-md bg-green-100">
                  <p className="text-xs text-gray-600">Correct</p>
                  <p className="text-xl font-semibold text-green-700">{summary.correct}</p>
                </div>
                <div className="p-3 rounded-md bg-red-50">
                  <p className="text-xs text-gray-600">Incorrect</p>
                  <p className="text-xl font-semibold text-red-700">{summary.incorrect}</p>
                </div>
                <div className="p-3 rounded-md bg-yellow-50">
                  <p className="text-xs text-gray-600">Unattempted</p>
                  <p className="text-xl font-semibold text-yellow-700">{summary.unattempted}</p>
                </div>
                <div className="p-3 rounded-md bg-blue-50">
                  <p className="text-xs text-gray-600">Total Score</p>
                  <p className="text-xl font-semibold text-blue-700">
                    {summary.totalScore.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Section-wise Scores</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={exportToCSV}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={() => setShowMarkingSchemeEditor(!showMarkingSchemeEditor)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {showMarkingSchemeEditor ? 'Hide' : 'Edit'} Marking Scheme
                  </button>
                </div>
              </div>
              
              {showMarkingSchemeEditor && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Custom Marking Schemes</h3>
                  <div className="space-y-3">
                    {Object.entries(
                      questions.reduce((acc, q) => {
                        if (!acc[q.questionType]) acc[q.questionType] = q.markingScheme;
                        return acc;
                      }, {})
                    ).map(([type, scheme]) => (
                      <div key={type} className="grid grid-cols-4 gap-3 items-center">
                        <label className="text-sm font-medium text-gray-700">{type}</label>
                        <div>
                          <label className="block text-xs text-gray-500">Correct</label>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={scheme.correct}
                            onChange={(e) => setCustomMarkingSchemes(prev => ({
                              ...prev,
                              [type]: { ...prev[type], correct: parseFloat(e.target.value) || 0 }
                            }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Incorrect</label>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={scheme.incorrect}
                            onChange={(e) => setCustomMarkingSchemes(prev => ({
                              ...prev,
                              [type]: { ...prev[type], incorrect: parseFloat(e.target.value) || 0 }
                            }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Unattempted</label>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={scheme.unattempted}
                            onChange={(e) => setCustomMarkingSchemes(prev => ({
                              ...prev,
                              [type]: { ...prev[type], unattempted: parseFloat(e.target.value) || 0 }
                            }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={recalculateWithCustomSchemes}
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Recalculate Scores
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(summary.sections).map(([name, sec]) => (
                  <div key={name} className="p-4 rounded-md border border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 mb-2">{name}</p>
                    <p className="text-xs text-gray-600">Questions: {sec.questions}</p>
                    <p className="text-xs text-gray-600">Attempted: {sec.attempted}</p>
                    <p className="text-xs text-gray-600">Correct: {sec.correct}</p>
                    <p className="text-xs text-gray-600">Incorrect: {sec.incorrect}</p>
                    <p className="text-xs text-gray-600">Score: {sec.score.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Parsed Questions</h2>
              <div className="overflow-auto">
                <table className="min-w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 border-b">Q#</th>
                      <th className="px-3 py-2 border-b">Section</th>
                      <th className="px-3 py-2 border-b">Type</th>
                      <th className="px-3 py-2 border-b">Status</th>
                      <th className="px-3 py-2 border-b">Your Answer</th>
                      <th className="px-3 py-2 border-b">Correct Answer</th>
                      <th className="px-3 py-2 border-b">Match %</th>
                      <th className="px-3 py-2 border-b">Result</th>
                      <th className="px-3 py-2 border-b">Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q, idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border-b font-medium">{q.questionNumber}</td>
                        <td className="px-3 py-2 border-b">{q.section}</td>
                        <td className="px-3 py-2 border-b">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {q.questionType}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b">{q.status}</td>
                        <td className="px-3 py-2 border-b max-w-xs truncate" title={q.chosen}>
                          {q.chosen || '-'}
                        </td>
                        <td className="px-3 py-2 border-b max-w-xs truncate" title={q.correct}>
                          {q.correct || '-'}
                        </td>
                        <td className="px-3 py-2 border-b">
                          {q.attempted ? (
                            <span className={`font-medium ${
                              parseFloat(q.similarity) > 0.8 ? 'text-green-600' : 
                              parseFloat(q.similarity) > 0.5 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {(parseFloat(q.similarity) * 100).toFixed(0)}%
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-3 py-2 border-b">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            q.attempted ? (
                              q.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            ) : 'bg-gray-100 text-gray-800'
                          }`}>
                            {q.attempted ? (q.isCorrect ? 'Correct' : 'Incorrect') : 'Unattempted'}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b">
                          <span className={`font-medium ${
                            q.marks > 0 ? 'text-green-600' : q.marks < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {q.marks > 0 ? '+' : ''}{Number(q.marks || 0).toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Marking Scheme Info */}
              {questions.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">Marking Scheme Applied</h3>
                  <div className="text-xs text-blue-700 space-y-1">
                    {Object.entries(
                      questions.reduce((acc, q) => {
                        const key = q.questionType;
                        if (!acc[key]) acc[key] = q.markingScheme;
                        return acc;
                      }, {})
                    ).map(([type, scheme]) => (
                      <div key={type}>
                        <strong>{type}:</strong> Correct: +{scheme.correct}, Incorrect: {scheme.incorrect}, Unattempted: {scheme.unattempted}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {htmlSource && (
          <details className="bg-white shadow rounded-lg p-4 text-sm text-gray-600">
            <summary className="cursor-pointer font-semibold text-gray-900">
              Raw HTML (debug)
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
              {htmlSource}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ScoreCalculator;
